import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import PlayingCard from "../components/PlayingCard";
import PlayerSeat from "../components/PlayerSeat";
import TradePanel from "../components/TradePanel";
import TradeAnimation from "../components/TradeAnimation";
import ResultsModal from "../components/ResultsModal";
import GameOverModal from "../components/GameOverModal";
import OutgoingTradeWindow from "../components/OutgoingTradeWindow";
import IncomingTradeBubble from "../components/IncomingTradeBubble";
import TradeLogModal from "../components/TradeLogModal";
import RulesModal from "../components/RulesModal";
import { Timer, Coins, Flag, TrendingUp, Award, ScrollText, HelpCircle } from "lucide-react";
import { getApiUrl, getWsUrl } from "../utils/api";
import { PHASE_MESSAGES } from "../constants/gameConfig";

const API = getApiUrl();
const WS_URL = getWsUrl();

export default function Game() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [myCards, setMyCards] = useState([]);
  const [selectedCardIndices, setSelectedCardIndices] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showTradePanel, setShowTradePanel] = useState(false);
  const [tradeTarget, setTradeTarget] = useState(null);
  const [pendingTrades, setPendingTrades] = useState([]);
  const [myPendingTrades, setMyPendingTrades] = useState([]); // Trades I sent
  const [roundResults, setRoundResults] = useState(null);
  const [gameOverResults, setGameOverResults] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [endVoteCount, setEndVoteCount] = useState(0);
  const [hasVotedEnd, setHasVotedEnd] = useState(false);
  const [nextRoundVotes, setNextRoundVotes] = useState(0);
  const [hasVotedNextRound, setHasVotedNextRound] = useState(false);
  const [phaseEndTime, setPhaseEndTime] = useState(null);
  const [tradeAnimation, setTradeAnimation] = useState(null);
  // Card status for my public cards: 0 = normal, 1 = 譲渡可, 2 = 譲渡不可
  const [myCardStatus, setMyCardStatus] = useState({});
  // Interest chips on other players' cards: { playerId: { cardIndex: true } }
  const [interestChips, setInterestChips] = useState({});
  // Chip request bubble shown from my profile
  const [showChipRequest, setShowChipRequest] = useState(false);
  // Disconnected players waiting list
  const [waitingForPlayers, setWaitingForPlayers] = useState([]);
  // Trade log modal
  const [showTradeLog, setShowTradeLog] = useState(false);
  const [tradeHistory, setTradeHistory] = useState([]);
  // Rules modal
  const [showRules, setShowRules] = useState(false);
  const wsRef = useRef(null);
  const pollingRef = useRef(null);
  const lastPhaseRef = useRef(null);

  const playerId = localStorage.getItem("playerId");
  const nickname = localStorage.getItem("nickname");

  const fetchGameState = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/rooms/${roomCode}?player_id=${playerId}`);
      const newState = response.data;
      
      // Detect phase changes for polling mode
      if (lastPhaseRef.current && lastPhaseRef.current !== newState.phase) {
        if (newState.phase === "reveal") {
          toast.info(PHASE_MESSAGES.REVEAL);
          setSelectedCardIndices([]);
          // Close results modal when new round starts
          setRoundResults(null);
          setNextRoundVotes(0);
          setHasVotedNextRound(false);
        } else if (newState.phase === "dealing") {
          // Also close modal during dealing phase (new round starting)
          setRoundResults(null);
          setNextRoundVotes(0);
          setHasVotedNextRound(false);
        } else if (newState.phase === "negotiation") {
          toast.info(PHASE_MESSAGES.NEGOTIATION);
          setEndVoteCount(0);
          setHasVotedEnd(false);
          // Close results modal if still open
          setRoundResults(null);
        } else if (newState.phase === "result") {
          // Show round results modal
          const latestResult = newState.round_results?.[newState.round_results.length - 1];
          if (latestResult && !roundResults) {
            setRoundResults({
              round: latestResult.round,
              results: latestResult.results
            });
            setPhaseEndTime(newState.phase_end_time);
            setNextRoundVotes(newState.next_round_votes?.length || 0);
            setHasVotedNextRound(newState.next_round_votes?.includes(playerId) || false);
          }
        } else if (newState.phase === "game_over") {
          // Close results modal and show game over
          setRoundResults(null);
          // Calculate final standings
          const standings = newState.players
            .map(p => ({ player_id: p.id, nickname: p.nickname, chips: p.chips, apparent: p.apparent, biz: p.biz, round_history: p.round_history }))
            .sort((a, b) => b.chips - a.chips)
            .map((s, i) => ({ ...s, final_rank: i + 1 }));
          setGameOverResults(standings);
          // Clear player data to prevent auto-rejoin to finished game
          localStorage.removeItem("playerId");
          localStorage.removeItem("isHost");
        }
      }
      
      // Also check for game_over phase on initial load
      if (!lastPhaseRef.current && newState.phase === "game_over" && !gameOverResults) {
        const standings = newState.players
          .map(p => ({ player_id: p.id, nickname: p.nickname, chips: p.chips, apparent: p.apparent, biz: p.biz, round_history: p.round_history }))
          .sort((a, b) => b.chips - a.chips)
          .map((s, i) => ({ ...s, final_rank: i + 1 }));
        setGameOverResults(standings);
        // Clear player data to prevent auto-rejoin to finished game
        localStorage.removeItem("playerId");
        localStorage.removeItem("isHost");
      }
      
      // Also check for result phase on initial load (no lastPhaseRef)
      if (!lastPhaseRef.current && newState.phase === "result" && !roundResults) {
        const latestResult = newState.round_results?.[newState.round_results.length - 1];
        if (latestResult) {
          setRoundResults({
            round: latestResult.round,
            results: latestResult.results
          });
          setPhaseEndTime(newState.phase_end_time);
          setNextRoundVotes(newState.next_round_votes?.length || 0);
          setHasVotedNextRound(newState.next_round_votes?.includes(playerId) || false);
        }
      }
      
      lastPhaseRef.current = newState.phase;
      
      setGameState(newState);
      
      // Update trade history for trade log
      if (newState.trade_history) {
        setTradeHistory(newState.trade_history);
      }
      
      // Update pending trades from polling response
      if (newState.pending_trades_for_me) {
        setPendingTrades(prev => {
          // Merge new trades, avoiding duplicates
          const existingIds = new Set(prev.map(t => t.id));
          const newTrades = newState.pending_trades_for_me.filter(t => !existingIds.has(t.id));
          if (newTrades.length > 0) {
            newTrades.forEach(t => {
              toast.info(`${t.from_nickname}から取引提案`);
            });
            return [...prev, ...newTrades];
          }
          // Remove trades that are no longer pending
          const pendingIds = new Set(newState.pending_trades_for_me.map(t => t.id));
          return prev.filter(t => pendingIds.has(t.id));
        });
      }
      
      // Update my pending trades (trades I sent)
      if (newState.my_pending_trades) {
        setMyPendingTrades(newState.my_pending_trades);
      }
      
      const me = newState.players?.find(p => p.id === playerId);
      if (me?.cards) {
        setMyCards(me.cards);
      }
      
      // If player is not in this game, redirect to home
      if (!me) {
        console.warn("Player not found in this game, redirecting to home");
        toast.error("このゲームに参加していません");
        navigate("/");
        return;
      }
    } catch (error) {
      console.error("Failed to fetch game state:", error);
      // If 404, the game doesn't exist - redirect to home
      if (error.response?.status === 404) {
        toast.error("ゲームが見つかりません");
        navigate("/");
      }
    }
  }, [roomCode, playerId, roundResults, navigate]);

  useEffect(() => {
    fetchGameState();
  }, [fetchGameState]);

  // Polling fallback when WebSocket fails
  // Always keep polling running to ensure we never miss state updates
  // WebSocket updates will still be applied immediately when received
  useEffect(() => {
    // Start polling every 1.5 seconds for game updates regardless of WebSocket state
    // This ensures we never get stuck if WebSocket fails silently
    pollingRef.current = setInterval(() => {
      fetchGameState();
    }, 1500);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchGameState]);

  useEffect(() => {
    if (!playerId || !roomCode) return;

    const websocket = new WebSocket(`${WS_URL}/ws/${roomCode}/${playerId}`);
    wsRef.current = websocket;
    
    websocket.onopen = () => {
      console.log("WebSocket connected");
      setWsConnected(true);
    };
    
    websocket.onerror = () => {
      console.log("WebSocket error - falling back to polling");
      setWsConnected(false);
    };
    
    websocket.onclose = () => {
      console.log("WebSocket closed - falling back to polling");
      setWsConnected(false);
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case "connected":
          setGameState(data.state);
          const me = data.state?.players?.find(p => p.id === playerId);
          if (me?.cards) setMyCards(me.cards);
          break;
          
        case "cards_dealt":
          setMyCards(data.cards);
          toast.info("カードが配られました");
          break;
          
        case "phase_change":
          setGameState(prev => ({ ...prev, phase: data.phase, phase_end_time: data.end_time }));
          if (data.phase === "reveal") {
            toast.info(PHASE_MESSAGES.REVEAL);
            setSelectedCardIndices([]);
          } else if (data.phase === "negotiation") {
            toast.info(PHASE_MESSAGES.NEGOTIATION);
            setEndVoteCount(0);
            setHasVotedEnd(false);
          }
          break;
          
        case "cards_revealed":
          fetchGameState();
          break;
          
        case "trade_offer":
          setPendingTrades(prev => [...prev, {
            ...data.trade,
            from_nickname: data.from_nickname,
            offer_cards_data: data.offer_cards_data
          }]);
          toast.info(`${data.from_nickname}から取引提案`);
          break;
          
        case "trade_completed":
          // Show trade animation (toast will be shown when animation completes)
          if (data.animation_data) {
            setTradeAnimation(data.animation_data);
          }
          // No toast here - animation onComplete or handleTradeResponse will handle it
          fetchGameState();
          break;
          
        case "trade_rejected":
          toast.error("取引が拒否されました");
          break;
        
        case "trade_conflict":
          toast.error(data.reason || "カードが既に取引されています");
          fetchGameState();
          break;
          
        case "round_result":
          setRoundResults(data);
          setPhaseEndTime(data.phase_end_time);
          setNextRoundVotes(0);
          setHasVotedNextRound(false);
          break;
          
        case "new_round":
          setMyCards(data.cards);
          setRoundResults(null);
          setSelectedCardIndices([]);
          setNextRoundVotes(0);
          setHasVotedNextRound(false);
          toast.info(`ラウンド ${data.round} 開始`);
          break;
          
        case "game_over":
          setGameOverResults(data.standings);
          // Clear player data to prevent auto-rejoin to finished game
          localStorage.removeItem("playerId");
          localStorage.removeItem("isHost");
          break;
        
        case "next_round_vote":
          setNextRoundVotes(data.vote_count);
          toast.info(`${data.nickname}が次のラウンドに投票 (${data.vote_count}/${data.total_players})`);
          break;
        
        case "quit_vote":
          toast.warning(`${data.nickname}がやめました`);
          break;
          
        case "player_disconnected":
          toast.warning(`${data.nickname || "プレイヤー"}が切断されました（60秒以内に再接続可能）`);
          fetchGameState();
          break;
        
        case "player_reconnected":
          toast.success(`${data.nickname || "プレイヤー"}が再接続しました`);
          fetchGameState();
          break;
        
        case "player_folded":
          toast.error(`${data.nickname || "プレイヤー"}がフォールドしました（タイムアウト）`);
          fetchGameState();
          break;
        
        case "end_negotiation_vote":
          setEndVoteCount(data.vote_count);
          toast.info(`${data.nickname}が交渉終了に投票 (${data.vote_count}/3)`);
          break;
        
        case "end_negotiation_vote_cancelled":
          setEndVoteCount(data.vote_count);
          toast.info(`${data.nickname}が投票を取り消し (${data.vote_count}/3)`);
          break;
        
        case "negotiation_ending":
          toast.success(data.message);
          break;
        
        case "card_status_update":
          // Update card status from other players
          fetchGameState();
          break;
        
        case "interest_chip_update":
          // Update interest chips from other players
          fetchGameState();
          break;
        
        case "chip_request_update":
          // Update chip request from other players
          fetchGameState();
          break;
        
        case "waiting_for_players":
          // Show disconnected players list
          setWaitingForPlayers(data.disconnected_players || []);
          toast.warning(data.message, { duration: 5000 });
          break;
        
        case "all_players_connected":
          // Clear waiting list
          setWaitingForPlayers([]);
          toast.success(data.message);
          break;
          
        default:
          break;
      }
    };

    // Ping to keep connection alive
    const pingInterval = setInterval(() => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      websocket.close();
    };
  }, [roomCode, playerId, fetchGameState]);

  // Timer countdown
  useEffect(() => {
    if (!gameState?.phase_end_time) return;

    const updateTimer = () => {
      const endTime = parseFloat(gameState.phase_end_time);
      const now = Date.now() / 1000;
      const remaining = Math.max(0, Math.floor(endTime - now));
      setTimeLeft(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [gameState?.phase_end_time]);

  const handleCardClick = (index) => {
    if (gameState?.phase !== "reveal") return;

    setSelectedCardIndices(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      if (prev.length >= 4) {
        return prev;
      }
      return [...prev, index];
    });
  };

  const handleRevealCards = async () => {
    if (selectedCardIndices.length !== 4) {
      toast.error("4枚のカードを選択してください");
      return;
    }

    try {
      await axios.post(`${API}/rooms/${roomCode}/reveal`, {
        player_id: playerId,
        public_indices: selectedCardIndices
      });
      
      // Update local cards
      setMyCards(prev => prev.map((card, i) => ({
        ...card,
        is_public: selectedCardIndices.includes(i)
      })));
      
      toast.success("カードを公開しました");
    } catch (error) {
      toast.error(error.response?.data?.detail || "エラーが発生しました");
    }
  };

  const handleTradeClick = (targetPlayer) => {
    setTradeTarget(targetPlayer);
    setShowTradePanel(true);
  };

  const handleTradeSubmit = async (tradeData) => {
    try {
      await axios.post(`${API}/rooms/${roomCode}/trade`, {
        from_player_id: playerId,
        to_player_id: tradeTarget.id,
        ...tradeData
      });
      
      toast.success("取引を提案しました");
      setShowTradePanel(false);
      setTradeTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "取引の提案に失敗しました");
    }
  };

  const handleTradeResponse = async (tradeId, accept) => {
    try {
      // Find the trade to get animation data before responding
      const trade = pendingTrades.find(t => t.id === tradeId);
      
      const response = await axios.post(`${API}/rooms/${roomCode}/trade/respond`, {
        player_id: playerId,
        trade_id: tradeId,
        accept
      });
      
      // Check response status
      const result = response.data;
      
      if (result.status === "conflict") {
        toast.error(result.reason || "取引が競合しました。カードは既に取引されています。");
        setPendingTrades(prev => prev.filter(t => t.id !== tradeId));
        fetchGameState();
        return;
      }
      
      if (result.status === "rejected") {
        toast.info("取引を拒否しました");
        setPendingTrades(prev => prev.filter(t => t.id !== tradeId));
        return;
      }
      
      // Only proceed with animation for accepted trades
      setPendingTrades(prev => prev.filter(t => t.id !== tradeId));
      
      if (accept && result.status === "accepted" && trade) {
        // Prepare animation data
        const fromPlayer = gameState?.players?.find(p => p.id === trade.from_player_id);
        const myPlayer = gameState?.players?.find(p => p.id === playerId);
        
        // Get cards involved in trade
        const offerCards = trade.offer_cards_data || [];
        const requestCards = trade.request_cards?.map(idx => myPlayer?.cards?.[idx]).filter(Boolean) || [];
        
        setTradeAnimation({
          fromPlayer: fromPlayer?.nickname || "相手",
          toPlayer: myPlayer?.nickname || "あなた",
          offerCards,
          requestCards,
          offerChips: trade.offer_chips || 0,
          requestChips: trade.request_chips || 0
        });
        
        // Toast will be shown when animation completes
      }
      
      fetchGameState();
    } catch (error) {
      toast.error(error.response?.data?.detail || "エラーが発生しました");
      fetchGameState();
    }
  };

  const handleEndNegotiation = async () => {
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/end-negotiation?player_id=${playerId}`);
      if (response.data.status === "voted") {
        setHasVotedEnd(true);
        toast.success(`交渉終了に投票しました (${response.data.vote_count}/3)`);
      } else {
        setHasVotedEnd(false);
        toast.info(`投票を取り消しました (${response.data.vote_count}/3)`);
      }
      setEndVoteCount(response.data.vote_count);
    } catch (error) {
      toast.error(error.response?.data?.detail || "投票に失敗しました");
    }
  };

  const handleNextGame = async () => {
    if (hasVotedNextRound) return;
    
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/vote-next-round?player_id=${playerId}`);
      setHasVotedNextRound(true);
      setNextRoundVotes(response.data.vote_count);
      toast.success(`次のラウンドに投票しました (${response.data.vote_count}/${response.data.total_players})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "投票に失敗しました");
    }
  };

  const handleQuit = async () => {
    try {
      await axios.post(`${API}/rooms/${roomCode}/vote-quit?player_id=${playerId}`);
      toast.info("ゲームを終了します");
    } catch (error) {
      toast.error(error.response?.data?.detail || "エラーが発生しました");
    }
  };

  // Toggle card status for my public cards: 0 -> 1 (譲渡可) -> 2 (譲渡不可) -> 0 (normal)
  const handleMyCardStatusToggle = async (cardIndex) => {
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/card-status?player_id=${playerId}&card_index=${cardIndex}`);
      // State will be updated via WebSocket broadcast
    } catch (error) {
      console.error("Failed to update card status:", error);
    }
  };

  // Toggle interest chip on other player's card
  const handleInterestChipToggle = async (targetPlayerId, cardIndex) => {
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/interest-chip?player_id=${playerId}&target_player_id=${targetPlayerId}&card_index=${cardIndex}`);
      // State will be updated via WebSocket broadcast
    } catch (error) {
      console.error("Failed to toggle interest chip:", error);
    }
  };

  // Toggle chip request bubble
  const handleChipRequestToggle = async () => {
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/chip-request?player_id=${playerId}`);
      // State will be updated via WebSocket broadcast
    } catch (error) {
      console.error("Failed to toggle chip request:", error);
    }
  };

  // Get card status label
  const getCardStatusLabel = (status) => {
    switch (status) {
      case 1: return "譲渡可";
      case 2: return "譲渡不可";
      default: return null;
    }
  };

  // Get my initial for interest chip
  const getMyInitial = () => {
    return nickname?.charAt(0)?.toUpperCase() || "?";
  };

  // Get interest chips placed by other players on my card
  const getInterestChipsOnMyCard = (cardIndex) => {
    const chips = [];
    if (!gameState?.players) return chips;
    
    for (const otherPlayer of gameState.players) {
      if (otherPlayer.id === playerId) continue;
      // Check if this player has placed interest on my card
      const interests = otherPlayer.interest_chips?.[playerId] || [];
      if (interests.includes(cardIndex)) {
        chips.push({
          playerId: otherPlayer.id,
          initial: otherPlayer.nickname?.[0]?.toUpperCase() || "?"
        });
      }
    }
    return chips;
  };

  const getPhaseText = () => {
    switch (gameState?.phase) {
      case "dealing": return "カード配布中";
      case "reveal": return "公開フェーズ";
      case "negotiation": return "交渉フェーズ";
      case "result": return "結果発表";
      case "game_over": return "ゲーム終了";
      default: return "";
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getOtherPlayers = () => {
    if (!gameState?.players) return { top: null, left: null, right: null };
    
    const others = gameState.players.filter(p => p.id !== playerId);
    return {
      top: others[0] || null,
      left: others[1] || null,
      right: others[2] || null
    };
  };

  // Get position for a player relative to the current player
  const getPlayerPosition = (targetPlayerId) => {
    const others = getOtherPlayers();
    if (others.top?.id === targetPlayerId) return "top";
    if (others.left?.id === targetPlayerId) return "left";
    if (others.right?.id === targetPlayerId) return "right";
    return "top";
  };

  // Map player position to outgoing trade window position
  const getOutgoingTradePosition = (targetPlayerId) => {
    const position = getPlayerPosition(targetPlayerId);
    switch (position) {
      case "top": return "right"; // Front player -> right side
      case "left": return "top-left";
      case "right": return "bottom-right";
      default: return "right";
    }
  };

  // Cancel a trade I sent
  const handleCancelTrade = async (tradeId) => {
    try {
      await axios.post(`${API}/rooms/${roomCode}/trade/cancel?trade_id=${tradeId}&player_id=${playerId}`);
      toast.success("取引を取り下げました");
      setMyPendingTrades(prev => prev.filter(t => t.id !== tradeId));
    } catch (error) {
      toast.error("取引の取り下げに失敗しました");
      console.error("Failed to cancel trade:", error);
    }
  };

  const myPlayer = gameState?.players?.find(p => p.id === playerId);
  const otherPlayers = getOtherPlayers();

  if (!gameState) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-brass border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="game-container min-h-screen relative overflow-hidden" data-testid="game-screen">
      {/* Landscape Mode Notice for Portrait */}
      <div className="landscape-notice">
        <div className="landscape-notice-icon">📱</div>
        <h2 className="text-xl font-display mb-2">横向きにしてください</h2>
        <p className="text-sm text-muted-foreground">
          このゲームは横向きでプレイすることを推奨しています
        </p>
      </div>

      {/* Background */}
      <div className="absolute inset-0 bg-background z-0" />
      
      {/* Poker Table */}
      <div className="absolute inset-8 md:inset-16 lg:inset-24 rounded-[50%] z-10 pointer-events-none" data-testid="poker-table">
        <div className="poker-table w-full h-full rounded-[50%]">
          <div className="spotlight absolute inset-0 rounded-[50%]" />
        </div>
      </div>

      {/* Game Instruction - Top Left Corner */}
      <div className="absolute top-1 left-3 z-30">
        <div className="bg-gradient-to-r from-amber-500/90 to-yellow-500/90 text-black font-bold px-3 py-1.5 rounded-full shadow-lg text-sm md:text-base tracking-wide">
          交渉してポーカーの強い手役をつくれ
        </div>
      </div>

      {/* Phase & Timer Header - Below instruction with spacing */}
      <div className="absolute top-12 left-3 z-30">
        <div className="phase-indicator px-3 py-2 flex items-center gap-2 md:gap-3" data-testid="phase-indicator">
          <Badge variant="outline" className="border-brass/50 text-brass font-display text-xs">
            R{gameState.current_round}
          </Badge>
          <span className="font-display text-sm whitespace-nowrap">{getPhaseText()}</span>
          {gameState.phase === "result" ? (
            // Result phase - timer managed by modal
            waitingForPlayers.length > 0 ? (
              <div className="flex items-center gap-1 font-data text-sm text-amber-400">
                <Timer className="w-3 h-3" />
                <span data-testid="timer">待機中</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 font-data text-sm text-muted-foreground">
                <Timer className="w-3 h-3" />
                <span data-testid="timer">結果表示</span>
              </div>
            )
          ) : timeLeft > 0 ? (
            <div className={`flex items-center gap-1 font-data text-sm ${timeLeft <= 10 ? "timer-danger" : timeLeft <= 30 ? "timer-warning" : ""}`}>
              <Timer className="w-3 h-3" />
              <span data-testid="timer">{formatTime(timeLeft)}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 font-data text-sm text-muted-foreground">
              <Timer className="w-3 h-3" />
              <span data-testid="timer">--:--</span>
            </div>
          )}
          
          {/* End Negotiation Button - Toggleable */}
          {gameState.phase === "negotiation" && (
            <Button
              size="sm"
              variant={hasVotedEnd ? "secondary" : "default"}
              onClick={handleEndNegotiation}
              className={`text-[10px] md:text-xs h-6 md:h-7 px-2 md:px-3 ${hasVotedEnd ? "bg-muted border-red-600 border text-red-400" : "bg-red-600 hover:bg-red-700 text-white border-none"}`}
              data-testid="end-negotiation-btn"
            >
              <Flag className="w-3 h-3 mr-1 hidden md:inline" />
              <span className="whitespace-nowrap">{hasVotedEnd ? "取消" : "終了"}</span>
              <span className="ml-1">({endVoteCount}/3)</span>
            </Button>
          )}
        </div>
        
        {/* Trade Log Button and Rules Button - Below timer */}
        <div className="flex items-center gap-2 mt-2">
          {(gameState.phase === "negotiation" || gameState.phase === "result") && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowTradeLog(true)}
              className="text-[10px] md:text-xs h-6 md:h-7 px-2 md:px-3 border-brass/30 text-brass hover:bg-brass/10"
            >
              <ScrollText className="w-3 h-3 mr-1" />
              <span className="whitespace-nowrap">取引ログ</span>
              {tradeHistory.length > 0 && (
                <span className="ml-1 text-muted-foreground">({tradeHistory.length})</span>
              )}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowRules(true)}
            className="text-[10px] md:text-xs h-6 md:h-7 px-2 md:px-3 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
          >
            <HelpCircle className="w-3 h-3 mr-1" />
            <span className="whitespace-nowrap">ルール/手役表</span>
          </Button>
        </div>
      </div>

      {/* Player Seats */}
      {/* Top Player - Positioned higher to make room for trade notifications */}
      {otherPlayers.top && (
        <div className="absolute top-2 md:top-4 left-1/2 -translate-x-1/2 z-30">
          <PlayerSeat
            player={otherPlayers.top}
            position="top"
            isNegotiationPhase={gameState.phase === "negotiation"}
            onTradeClick={() => handleTradeClick(otherPlayers.top)}
            onCardClick={(cardIndex) => handleInterestChipToggle(otherPlayers.top.id, cardIndex)}
            myInitial={getMyInitial()}
            allPlayers={gameState.players || []}
          />
        </div>
      )}

      {/* Left Player - Vertical layout */}
      {otherPlayers.left && (
        <div className="absolute left-2 md:left-4 top-[30%] md:top-1/3 z-30">
          <PlayerSeat
            player={otherPlayers.left}
            position="left"
            isNegotiationPhase={gameState.phase === "negotiation"}
            onTradeClick={() => handleTradeClick(otherPlayers.left)}
            onCardClick={(cardIndex) => handleInterestChipToggle(otherPlayers.left.id, cardIndex)}
            myInitial={getMyInitial()}
            allPlayers={gameState.players || []}
          />
        </div>
      )}

      {/* Right Player - Vertical layout */}
      {otherPlayers.right && (
        <div className="absolute right-2 md:right-4 top-[30%] md:top-1/3 z-30">
          <PlayerSeat
            player={otherPlayers.right}
            position="right"
            isNegotiationPhase={gameState.phase === "negotiation"}
            onTradeClick={() => handleTradeClick(otherPlayers.right)}
            onCardClick={(cardIndex) => handleInterestChipToggle(otherPlayers.right.id, cardIndex)}
            myInitial={getMyInitial()}
            allPlayers={gameState.players || []}
          />
        </div>
      )}

      {/* My Cards (Bottom) */}
      <div className="absolute bottom-0 left-0 right-0 z-20" data-testid="my-cards-area">
        <div className="bg-gradient-to-t from-background via-background/90 to-transparent pt-8 pb-4 px-4">
          {/* Card Display - Split into Public (front) and Private (back) */}
          {gameState.phase === "reveal" ? (
            /* Reveal Phase - Selection UI: Public on top, Hand at bottom */
            <div className="space-y-4">
              {/* Instructions */}
              <div className="text-center text-sm text-muted-foreground">
                確定する4枚を選択してください（選択したカードが公開されます）
              </div>
              
              {/* Top Row: Selected for Public */}
              <div className="text-center">
                <div className="text-xs text-brass mb-2 uppercase tracking-wider">
                  公開カード（テーブルに出す）: {selectedCardIndices.length}/4
                </div>
                <div className="flex justify-center items-end gap-2 min-h-[120px] p-3 border border-brass/30 rounded-lg bg-brass/5">
                  <AnimatePresence>
                    {selectedCardIndices.map((index) => {
                      const card = myCards[index];
                      return (
                        <motion.div
                          key={`public-${index}`}
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.8, y: 20 }}
                          onClick={() => handleCardClick(index)}
                          className="cursor-pointer hover:scale-105 transition-transform"
                        >
                          <PlayingCard
                            suit={card.suit}
                            rank={card.rank}
                            isPublic={true}
                            size="lg"
                          />
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  {selectedCardIndices.length === 0 && (
                    <div className="text-muted-foreground text-sm">下のカードをクリックして選択</div>
                  )}
                </div>
              </div>
              
              {/* Bottom Row: Remaining Cards (Hand) + Confirm Button */}
              <div className="flex items-end justify-center gap-4">
                {/* Hand Cards */}
                <div className="text-center">
                  <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">
                    手札（非公開）: {myCards.length - selectedCardIndices.length}枚
                  </div>
                  <div className="flex justify-center items-end gap-1 md:gap-2">
                    <AnimatePresence>
                      {myCards.map((card, index) => {
                        if (selectedCardIndices.includes(index)) return null;
                        return (
                          <motion.div
                            key={`hand-${index}`}
                            layout
                            onClick={() => handleCardClick(index)}
                            className="cursor-pointer hover:-translate-y-2 transition-transform"
                          >
                            <PlayingCard
                              suit={card.suit}
                              rank={card.rank}
                              isPublic={false}
                              size="lg"
                            />
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                </div>
                
                {/* Confirm Button */}
                <Button
                  data-testid="reveal-btn"
                  onClick={handleRevealCards}
                  disabled={selectedCardIndices.length !== 4}
                  className="bg-brass text-accent-foreground hover:bg-brass/90 h-12"
                >
                  {selectedCardIndices.length === 4 ? "確定" : `${selectedCardIndices.length}/4`}
                </Button>
              </div>
            </div>
          ) : (
            /* Normal Play - Public cards on top, Profile + Private cards at bottom */
            <div className="space-y-2">
              {/* Top Row: Public Cards with tap status */}
              {myCards.filter(c => c.is_public).length > 0 && (
                <div className="text-center">
                  <div className="text-xs text-brass mb-1 uppercase tracking-wider flex items-center justify-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-brass"></span>
                    {gameState.phase === "negotiation" ? "公開中（タップで状態変更）" : "公開中"}
                  </div>
                  <div className="flex justify-center items-end gap-1 md:gap-2">
                    <AnimatePresence>
                      {myCards.map((card, index) => {
                        if (!card.is_public) return null;
                        // Get status from server state (key is string in JSON)
                        const status = myPlayer?.card_status?.[String(index)] || myPlayer?.card_status?.[index] || 0;
                        const statusLabel = getCardStatusLabel(status);
                        // Get interest chips from other players on this card
                        const interestChips = getInterestChipsOnMyCard(index);
                        const isClickable = gameState.phase === "negotiation";
                        return (
                          <motion.div
                            key={`public-${index}`}
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`relative ${isClickable ? "cursor-pointer" : ""}`}
                            onClick={() => isClickable && handleMyCardStatusToggle(index)}
                          >
                            <PlayingCard
                              suit={card.suit}
                              rank={card.rank}
                              isPublic={true}
                              size="lg"
                            />
                            {/* Status label below card */}
                            {statusLabel && (
                              <div className={`absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${
                                status === 1 ? "bg-green-600 text-white" : "bg-red-600 text-white"
                              }`}>
                                {statusLabel}
                              </div>
                            )}
                            {/* Interest chips from other players */}
                            {interestChips.length > 0 && (
                              <div className="absolute -top-2 -right-2 flex gap-0.5">
                                {interestChips.slice(0, 3).map((chip, chipIdx) => (
                                  <div 
                                    key={chipIdx}
                                    className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-[8px] font-bold text-white shadow-md border border-white z-10"
                                    title={`${chip.initial}が興味あり`}
                                  >
                                    {chip.initial}
                                  </div>
                                ))}
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {/* Bottom Row: Profile + Private Cards */}
              <div className="flex items-center justify-center gap-2 md:gap-4">
                {/* My Profile Card - Left side with chip request bubble */}
                {myPlayer && (
                  <div className="relative flex-shrink-0">
                    {/* Chip Request Bubble - from server state */}
                    {myPlayer.chip_request && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute -top-10 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap z-10"
                      >
                        チップ希望
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-amber-500 rotate-45"></div>
                      </motion.div>
                    )}
                    <div 
                      onClick={handleChipRequestToggle}
                      className="bg-card/95 backdrop-blur-sm border-2 border-brass/50 rounded-lg px-2 md:px-3 py-1.5 md:py-2 min-w-[100px] md:min-w-[130px] shadow-lg cursor-pointer hover:border-brass transition-colors"
                    >
                      <div className="flex items-center justify-between gap-1 md:gap-2">
                        <span className="font-medium text-xs md:text-sm truncate max-w-[60px] md:max-w-[80px]">{myPlayer.nickname}</span>
                        <Badge variant="outline" className="text-[8px] md:text-[10px] px-1 py-0 border-brass/30 text-brass">あなた</Badge>
                      </div>
                      <div className="flex items-center justify-between mt-0.5 md:mt-1">
                        <div className="flex items-center gap-1 text-brass font-data text-xs md:text-sm">
                          <Coins className="w-3 h-3 md:w-4 md:h-4" />
                          <span data-testid="my-chips">{myPlayer.chips}</span>
                        </div>
                        <span className="text-[10px] md:text-xs text-muted-foreground">
                          {myCards.filter(c => c.is_public).length}/{myCards.length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1 pt-1 border-t border-border/50 gap-1">
                        <div className="flex items-center gap-0.5 text-[10px]" title="みかけレート">
                          <TrendingUp className="w-2.5 h-2.5 text-blue-400 flex-shrink-0" />
                          <span className="text-blue-400 font-data">{Math.round(myPlayer.apparent || 1500)}</span>
                        </div>
                        <div className="flex items-center gap-0.5 text-[10px]" title="商才">
                          <Award className="w-2.5 h-2.5 text-amber-400 flex-shrink-0" />
                          <span className="text-amber-400 font-data">{Math.round(myPlayer.biz || 0)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Private Cards (Hand) */}
                {myCards.filter(c => !c.is_public).length > 0 && (
                  <div className="text-center">
                    <div className="text-[10px] md:text-xs text-muted-foreground mb-1 uppercase tracking-wider flex items-center justify-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                      手札（非公開）
                    </div>
                    <div className="flex justify-center items-end gap-0.5 md:gap-1">
                      <AnimatePresence>
                        {myCards.map((card, index) => {
                          if (card.is_public) return null;
                          return (
                            <motion.div
                              key={`private-${index}`}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                            >
                              <PlayingCard
                                suit={card.suit}
                                rank={card.rank}
                                isPublic={false}
                                size="md"
                              />
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Incoming Trade Bubbles (from other players) */}
      {pendingTrades.map((trade, index) => {
        // Group by sender for stacking
        const sameSenderTrades = pendingTrades.filter(t => t.from_player_id === trade.from_player_id);
        const stackIndex = sameSenderTrades.findIndex(t => t.id === trade.id);
        
        return (
          <IncomingTradeBubble
            key={trade.id}
            trade={trade}
            senderPosition={getPlayerPosition(trade.from_player_id)}
            stackIndex={stackIndex}
            onAccept={(tradeId) => handleTradeResponse(tradeId, true)}
            onReject={(tradeId) => handleTradeResponse(tradeId, false)}
          />
        );
      })}

      {/* Outgoing Trade Windows (trades I sent) */}
      {myPendingTrades.map((trade, index) => {
        // Group by target for stacking
        const sameTargetTrades = myPendingTrades.filter(t => t.to_player_id === trade.to_player_id);
        const stackIndex = sameTargetTrades.findIndex(t => t.id === trade.id);
        
        return (
          <OutgoingTradeWindow
            key={trade.id}
            trade={trade}
            position={getOutgoingTradePosition(trade.to_player_id)}
            stackIndex={stackIndex}
            onCancel={handleCancelTrade}
          />
        );
      })}

      {/* Trade Panel */}
      {showTradePanel && tradeTarget && (
        <TradePanel
          targetPlayer={tradeTarget}
          myCards={myCards}
          myChips={myPlayer?.chips || 0}
          onSubmit={handleTradeSubmit}
          onClose={() => {
            setShowTradePanel(false);
            setTradeTarget(null);
          }}
        />
      )}

      {/* Trade Animation */}
      <AnimatePresence>
        {tradeAnimation && (
          <TradeAnimation
            tradeData={tradeAnimation}
            onComplete={() => {
              setTradeAnimation(null);
              toast.success("取引が成立しました");
            }}
          />
        )}
      </AnimatePresence>

      {/* Trade Log Modal */}
      <TradeLogModal
        isOpen={showTradeLog}
        onClose={() => setShowTradeLog(false)}
        trades={tradeHistory}
        players={gameState?.players || []}
      />

      {/* Rules Modal */}
      <RulesModal
        open={showRules}
        onClose={() => setShowRules(false)}
      />

      {/* Results Modal */}
      {roundResults && (
        <ResultsModal
          results={roundResults}
          onVoteNextRound={handleNextGame}
          onQuit={handleQuit}
          nextRoundVotes={nextRoundVotes}
          totalPlayers={gameState?.players?.length || 4}
          hasVoted={hasVotedNextRound}
          phaseEndTime={phaseEndTime}
          waitingForPlayers={waitingForPlayers}
        />
      )}

      {/* Game Over Modal */}
      {gameOverResults && (
        <GameOverModal
          standings={gameOverResults}
          onClose={() => navigate("/")}
        />
      )}
    </div>
  );
}
